import React, { useContext, useEffect, useState } from "react";
import { useFormik } from "formik";
import { Grid } from "@mui/material";

// custom imports
import PageHeader from "../../components/PageHeader";
import CustomTable from "../../components/Tables/CustomTable";
import FormDialog from "../../forms/FormDialog";
import CustomInput from "../../components/SecondaryForms/CustomInput";
import FormInput from "../../forms/FormInput";

// services
import URLS from "../../services/urlConstants";
import { get, put, post, del } from "../../services/apiMethods";

// data
import { ownerCodeKeys } from "../../data/TableData/ownerCode";
import CustomSelect from "../../components/SecondaryForms/CustomSelect";
import WarehouseContext from "../../context/WarehouseContext";
import CustomSearch from "../../components/Tables/CustomSearch";

export default function OwnerCode() {
  const [open, setOpen] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [dataToEdit, setDataToEdit] = useState(null);
  const [data, setData] = useState([]);
  const [formFields, setFormFields] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ownerGroup, setOwnerGroup] = useState([]);
  const [selectedOwnerGroup, setSelectedOwnerGroup] = useState("");
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState("");
  const [searchFilter, setSearchFilter] = useState([]);
  const [search, setSearch] = useState("");
  const { allWarehouses } = useContext(WarehouseContext);

  const [readOnly, setReadOnly] = useState(false);

  useEffect(() => {
    setWarehouses(
      allWarehouses?.map((s) => ({
        ...s,
        label: s.code,
        value: s._id,
      })) || []
    );

    setSelectedWarehouse(allWarehouses[0]?._id || "");
  }, [allWarehouses]);

  const getData = async () => {
    setIsReady(false);
    try {
      const { data } = await get(URLS.ownerCode.list, {
        params: { ownerGroup: selectedOwnerGroup },
      });

      setData(data.result);
    } catch (error) {
      console.log(error);
    }
    setTimeout(() => {
      setIsReady(true);
    }, 300);
  };

  const getFormDetails = async () => {
    try {
      const { data } = await get(URLS.forms.read, {
        params: { collectionName: "ownerCode", warehouse: selectedWarehouse },
      });
      setFormFields(data.result ? data.result.fields : null);
    } catch (error) {
      console.log(error);
    }
  };

  const getOwnerGroupList = async () => {
    try {
      const { data } = await get(URLS.ownerGroup.list, {
        params: { warehouse: selectedWarehouse },
      });

      setOwnerGroup(
        data.result.map((group) => ({
          ...group,
          label: group.name,
          value: group._id,
        }))
      );
      setSelectedOwnerGroup(data.result[0]?._id || "");
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (selectedOwnerGroup) {
      getData();
      getFormDetails();
    } else {
      setIsReady(true);
    }
  }, [selectedOwnerGroup]);

  useEffect(() => {
    if (selectedWarehouse) {
      getOwnerGroupList();
    }
  }, [selectedWarehouse]);

  const handleOnFieldAdd = () => {
    getData();
    getFormDetails();
  };

  // create || update actions
  const handleCreateOrUpdate = async (values) => {
    try {
      const payload = {
        ...values,
        meta: formFields,
      };
      setLoading(true);

      if (dataToEdit) {
        const { data } = await put(
          URLS.ownerCode.update + "/" + dataToEdit._id,
          payload
        );
      } else {
        const { data } = await post(URLS.ownerCode.create, payload);
      }
      getData();
      handleModalClose();
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
    setLoading(false);
  };

  const formik = useFormik({
    initialValues: {
      name: dataToEdit?.name || "",
      description: dataToEdit?.description || "",
      ownerGroup: selectedOwnerGroup || "",
    },
    onSubmit: (values) => handleCreateOrUpdate(values),
    enableReinitialize: true,
  });

  // modal actionss
  const openDataAddOrUpdateModal = () => setOpen(true);

  const handleModalClose = () => {
    setOpen(false);
    setTimeout(() => {
      setDataToEdit(null);
      setFormFields(
        formFields?.map((f) => ({
          ...f,
          value: "",
        }))
      );
      formik.resetForm();
    }, 200);
  };

  // edit data actions
  const handleCustomInputChange = (e) => {
    const { name, value } = e.target;
    setFormFields(
      formFields?.map((f) => (f.inputLabel === name ? { ...f, value } : f))
    );
  };

  const handleSetDataToEdit = (data) => {
    setReadOnly(false);
    setDataToEdit(data);
    const fields = data?.meta;
    setFormFields(
      formFields?.map((f) => ({
        ...f,
        value:
          fields?.find((fi) => fi.inputLabel === f.inputLabel)?.value || "",
      }))
    );
    setOpen(true);
  };

  const handleShowData = (data) => {
    setReadOnly(true);
    setDataToEdit(data);
    const fields = data?.meta;
    setFormFields(
      formFields?.map((f) => ({
        ...f,
        value:
          fields?.find((fi) => fi.inputLabel === f.inputLabel)?.value || "",
      }))
    );
    setOpen(true);
  };
  const handleDelete = async (id, reason) => {
    try {
      const response = await del(URLS.ownerCode.delete + "/" + id, {
        params: {
          reasonForDelete: reason,
        },
      });
      const filtered = data.filter((item) => item._id !== id);
      setData(filtered);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <PageHeader
        collection="Owner Code"
        onAddClick={openDataAddOrUpdateModal}
        title="Owner Code"
        modelName="ownerCode"
        onFieldAdd={handleOnFieldAdd}
        module="Owner code"
        addButtonDisabled={!selectedOwnerGroup}
      />
      <Grid
        container
        spacing={2}
        my={2}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <Grid item xs={12} md={6} lg={3}>
          <CustomSelect
            required={true}
            name="warehouse"
            onChange={(e) => setSelectedWarehouse(e.target.value)}
            label="Select Warehouse"
            options={warehouses}
            value={selectedWarehouse}
          />
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <CustomSelect
            required={true}
            name="ownerGroup"
            onChange={(e) => setSelectedOwnerGroup(e.target.value)}
            label="Select Owner Group"
            options={ownerGroup}
            value={selectedOwnerGroup}
          />
        </Grid>

        <Grid item xs={12} md={6} lg={3} marginTop={2}>
          <CustomSearch
            data={data}
            setSearch={setSearch}
            setSearchFilter={setSearchFilter}
            search={search}
            placeholder="Code"
            keys={["name"]}
          />
        </Grid>
      </Grid>
      <CustomTable
        actions={["edit", "delete", "view"]}
        bodyDataModal="owner code"
        isDataReady={isReady}
        bodyData={search ? searchFilter : data}
        tableKeys={ownerCodeKeys}
        onEditClick={handleSetDataToEdit}
        onDeleteClick={handleDelete}
        onViewClick={handleShowData}
        module="Owner code"
        formFields={formFields}
      />

      <FormDialog
        readOnly={readOnly}
        open={open}
        onClose={handleModalClose}
        submitButtonTitle={dataToEdit ? "Update" : "Add"}
        formik={formik}
        formTitle={dataToEdit ? "Update owner code" : "Add owner code"}
        adding={loading}
      >
        <Grid container rowSpacing={1} columnSpacing={2}>
          <Grid item xs={12} sm={6}>
            <FormInput
              required={true}
              name="name"
              type="text"
              formik={formik}
              label="Code"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormInput
              name="description"
              type="text"
              formik={formik}
              label="Description"
            />
          </Grid>
          {formFields
            ? formFields?.map((f, i) => (
                <Grid key={i} item xs={12} sm={6}>
                  <CustomInput
                    onChange={handleCustomInputChange}
                    name={f.inputLabel}
                    value={f.value}
                    required={f.isRequired}
                    label={f.inputLabel}
                    type={f.inputType}
                  />
                </Grid>
              ))
            : null}
        </Grid>
      </FormDialog>
    </>
  );
}
